import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  DatePicker,
  FormField,
  Input,
  PeopleField,
  Select,
  Textarea,
  toast,
  type PeopleFieldValue,
} from "@playbookai/ui";
import { useCreateKpi } from "../hooks/useCreateKpi.js";
import { useKpiFieldErrors } from "../hooks/useKpiFieldErrors.js";
import { useKpiCategoryOptions } from "../hooks/useKpiCategoryOptions.js";
import { useKpiDefinitions } from "../hooks/useKpiDefinitions.js";
import { useKpiPeopleSource } from "../owner/use-kpi-people-source.js";
import {
  KPI_DIRECTIONS,
  KPI_REPORTING_FREQUENCIES,
  type CreateKpiInput,
  type KpiDefinition,
  type KpiDirection,
  type ManagedKpiReportingFrequency,
} from "../api/types.js";
import { ClearableCombobox } from "./ClearableCombobox.js";
import { FormSection } from "./FormSection.js";
import {
  DIRECTION_LABEL,
  REPORTING_FREQUENCY_LABEL,
} from "./kpi-presentation.js";
import { errorMessage } from "./error-message.js";
import { kpiTargetFigureError } from "./kpi-observation-value.js";

export interface CreateKpiPageProps {
  /** Definition selected in the KPI Library, applied ONCE at mount — the Type
   * remains changeable, and a re-entry after a discard prompt must not clobber
   * what the user already typed. */
  readonly initialDefinitionId?: string | null;
  readonly onCancel: () => void;
  /** The "All KPIs" link. Without it the link cancels, as before. */
  readonly onBackToAll?: () => void;
  readonly onCreated: (kpiId: string) => void;
  /** Reported whenever the edited-vs-pristine state flips, so the host can ask
   * before an unsaved draft is discarded. */
  readonly onDirtyChange?: (dirty: boolean) => void;
}

/** The measurement shape a KPI Type contributes when it is chosen. Kept as one
 * value because the three always move together — applying a template must never
 * leave a KPI half-templated. */
interface TemplateDefaults {
  unit: string;
  direction: string;
  categoryCode: string;
}

const NO_TEMPLATE: TemplateDefaults = {
  unit: "",
  direction: "",
  categoryCode: "",
};

function templateOf(definition: KpiDefinition): TemplateDefaults {
  return {
    unit: definition.unit,
    direction: definition.direction,
    // A Type with no installed catalog entry carries no category; leave the
    // field alone rather than blanking a choice the user may have made.
    categoryCode: definition.category?.code ?? "",
  };
}

const DIRECTION_OPTIONS = KPI_DIRECTIONS.map((direction) => ({
  value: direction,
  label: DIRECTION_LABEL[direction],
}));

const FREQUENCY_OPTIONS = KPI_REPORTING_FREQUENCIES.map((frequency) => ({
  value: frequency,
  label: REPORTING_FREQUENCY_LABEL[frequency],
}));

/**
 * Create a configured KPI (`POST /v1/kpis`), as a dedicated page under the KPI
 * Manager rail. One page, four groups, no wizard: everything a KPI needs is
 * visible at once, and only Name — plus a unit and direction when no KPI Type is
 * chosen — is required.
 *
 * A KPI Type is a TEMPLATE. Choosing one pre-fills Unit, Direction and Category,
 * which stay visible and editable; clearing the Type leaves those values alone,
 * because they are the KPI's own facts once they are on screen. The Target to
 * Improve is optional and progressively disclosed, all-or-nothing when supplied.
 * The KPI always starts `draft` — the page says so and offers no lifecycle
 * control (the server would refuse one anyway).
 */
export function CreateKpiPage({
  initialDefinitionId,
  onCancel,
  onBackToAll,
  onCreated,
  onDirtyChange,
}: CreateKpiPageProps): JSX.Element {
  const peopleSource = useKpiPeopleSource();
  const definitionsQuery = useKpiDefinitions();
  const categoriesQuery = useKpiCategoryOptions();
  const create = useCreateKpi();

  // Frozen at mount: the pristine baseline every field is compared against.
  const baselineDefinitionId = useRef(initialDefinitionId ?? "").current;

  const [definitionId, setDefinitionId] = useState(baselineDefinitionId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [direction, setDirection] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [reportingFrequency, setReportingFrequency] = useState("");
  const [owner, setOwner] = useState<PeopleFieldValue>(null);
  const [baselineValue, setBaselineValue] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const validation = useKpiFieldErrors("create", {
    name,
    description,
    definitionId,
    unit,
    direction,
    categoryCode,
    reportingFrequency,
    ownerUserId: owner?.kind === "person" ? owner.id : "",
    "target.baselineValue": baselineValue,
    "target.targetValue": targetValue,
    "target.targetDate": targetDate,
  });

  // What the mount-time Type pre-filled, so an untouched template still reads
  // pristine. It moves ONLY when that first template lands; every later change
  // (including picking a different Type) is the user's and is therefore dirty.
  const [templateBaseline, setTemplateBaseline] =
    useState<TemplateDefaults>(NO_TEMPLATE);
  const initialTemplateApplied = useRef(false);

  const definitions = useMemo(
    () => definitionsQuery.data ?? [],
    [definitionsQuery.data],
  );

  // The Library's chosen Type arrives before the catalog does, so its defaults
  // are applied once the catalog resolves — and only once, so a re-render (or a
  // return from the discard prompt) never overwrites what the user typed.
  useEffect(() => {
    if (initialTemplateApplied.current || !baselineDefinitionId) return;
    const definition = definitions.find(
      (d) => d.definitionId === baselineDefinitionId,
    );
    if (!definition) return;
    initialTemplateApplied.current = true;
    const template = templateOf(definition);
    setUnit(template.unit);
    setDirection(template.direction);
    if (template.categoryCode) setCategoryCode(template.categoryCode);
    setTemplateBaseline(template);
  }, [baselineDefinitionId, definitions]);

  const typeOptions = useMemo(
    () =>
      definitions.map((d) => ({
        value: d.definitionId,
        label: `${d.name} (${d.unit})`,
      })),
    [definitions],
  );

  const categoryOptions = useMemo(() => {
    const options = (categoriesQuery.data ?? []).map((option) => ({
      value: option.code,
      // A code the installed catalog no longer describes still has to be
      // pickable; showing the code is truthful, showing nothing is not.
      label: option.name ?? option.code,
    }));
    // A Type's category can be selected before the option set has loaded (or
    // could belong to a pack this list does not know); without this the
    // Combobox would render an empty box over a real selection.
    if (categoryCode && !options.some((o) => o.value === categoryCode)) {
      options.push({ value: categoryCode, label: categoryCode });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [categoriesQuery.data, categoryCode]);

  /** Applying a Type overwrites the measurement shape; CLEARING one does not.
   * A cleared Type means "this is my own KPI now", not "forget what it is".
   *
   * A Type with NO category leaves the Category field alone, the same way the
   * mount-time application does — `templateOf`'s own note says so, and this path
   * used to contradict it by assigning the empty string. Unit and Direction are
   * different: a Type always states both, so there is no "the template said
   * nothing" case for them to preserve through. */
  const handleTypeChange = (nextDefinitionId: string) => {
    setDefinitionId(nextDefinitionId);
    const definition = definitions.find(
      (d) => d.definitionId === nextDefinitionId,
    );
    if (!definition) return;
    const template = templateOf(definition);
    setUnit(template.unit);
    setDirection(template.direction);
    if (template.categoryCode) setCategoryCode(template.categoryCode);
  };

  const trimmedName = name.trim();
  const trimmedUnit = unit.trim();
  const trimmedBaseline = baselineValue.trim();
  const trimmedTarget = targetValue.trim();

  // Without a Type there is nothing to inherit from, so the KPI must state its
  // own measurement shape. The server re-checks this — it is the authority.
  const measurementRequired = !definitionId;
  const nameError =
    (nameTouched || submitted) && !trimmedName
      ? "Name is required."
      : undefined;
  const unitError =
    submitted && measurementRequired && !trimmedUnit
      ? "A unit is required when no KPI Template is selected."
      : undefined;
  const directionError =
    submitted && measurementRequired && !direction
      ? "A direction is required when no KPI Template is selected."
      : undefined;

  // All three or none. A partially-filled target is the one thing here that
  // needs explaining rather than blocking, so the button stays enabled and the
  // reason is shown on submit.
  const ttiFields = [trimmedBaseline, trimmedTarget, targetDate];
  const ttiStarted = ttiFields.some(Boolean);
  const ttiComplete = ttiFields.every(Boolean);
  const ttiIncomplete = ttiStarted && !ttiComplete;
  const ttiError = (field: string) =>
    submitted && ttiIncomplete && !field
      ? "Required once a target to improve is started."
      : undefined;
  // The figures every month is scored against must be numbers (KS-08), in the
  // same words as the TTI page's monthly box. Nothing is stored yet, so every
  // typed value is judged.
  const baselineNotNumber = kpiTargetFigureError(trimmedBaseline);
  const targetNotNumber = kpiTargetFigureError(trimmedTarget);

  /**
   * Name is the only gate on the button. Everything else — the measurement
   * shape a Type-less KPI must state, and the all-or-nothing target — is
   * checked ON SUBMIT and answered with a specific message next to the field
   * that is missing. A button disabled with no stated reason is the pattern
   * `packages/ui/CLAUDE.md` calls out; here the form always says what it wants.
   */
  const measurementMissing =
    measurementRequired && (!trimmedUnit || !direction);
  const canSubmit = Boolean(trimmedName) && !create.isPending;

  const dirty =
    definitionId !== baselineDefinitionId ||
    name !== "" ||
    description !== "" ||
    unit !== templateBaseline.unit ||
    direction !== templateBaseline.direction ||
    categoryCode !== templateBaseline.categoryCode ||
    reportingFrequency !== "" ||
    owner !== null ||
    ttiStarted;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Refresh / close / an off-SPA link: the only exits the host's own prompt
  // cannot reach, so hand them to the browser's native guard.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setNameTouched(true);
    if (
      !canSubmit ||
      measurementMissing ||
      ttiIncomplete ||
      baselineNotNumber ||
      targetNotNumber
    )
      return;

    const input: { -readonly [K in keyof CreateKpiInput]?: CreateKpiInput[K] } =
      { name: trimmedName };
    if (definitionId) input.definitionId = definitionId;
    if (description.trim()) input.description = description.trim();
    // Sent whenever present, Type or no Type: the field stays editable after a
    // template lands, so the value on screen is the one that must be saved.
    if (trimmedUnit) input.unit = trimmedUnit;
    if (direction) input.direction = direction as KpiDirection;
    if (categoryCode) input.categoryCode = categoryCode;
    if (reportingFrequency) {
      input.reportingFrequency =
        reportingFrequency as ManagedKpiReportingFrequency;
    }
    if (owner?.kind === "person") input.ownerUserId = owner.id;
    if (ttiComplete) {
      input.target = {
        baselineValue: trimmedBaseline,
        targetValue: trimmedTarget,
        targetDate,
      };
    }

    const request = validation.beginRequest();
    create.mutate(input as CreateKpiInput, {
      onSuccess: (kpi) => {
        if (!request.isCurrent()) return;
        toast("KPI created", { tone: "healthy" });
        onCreated(kpi.kpiId);
      },
      onError: (err) => {
        if (!request.reject(err)) return;
        toast(errorMessage(err, "Could not create the KPI."), {
          tone: "critical",
        });
      },
    });
  };

  const targetSection = (
    <div className="space-y-4 pt-1">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Baseline value"
          error={
            ttiError(trimmedBaseline) ??
            baselineNotNumber ??
            validation.errors["target.baselineValue"]
          }
          hint="Where the measure stands today."
        >
          <Input
            value={baselineValue}
            onChange={(event) => setBaselineValue(event.target.value)}
            maxLength={255}
            placeholder="e.g. 82"
          />
        </FormField>
        <FormField
          label="Target value"
          error={
            ttiError(trimmedTarget) ??
            targetNotNumber ??
            validation.errors["target.targetValue"]
          }
        >
          <Input
            value={targetValue}
            onChange={(event) => setTargetValue(event.target.value)}
            maxLength={255}
            placeholder="e.g. 95"
          />
        </FormField>
      </div>
      <FormField
        label="Improve by"
        error={ttiError(targetDate) ?? validation.errors["target.targetDate"]}
      >
        <DatePicker
          value={targetDate}
          onChange={setTargetDate}
          placeholder="Pick a target date"
        />
      </FormField>
    </div>
  );

  return (
    <section
      aria-labelledby="create-kpi-heading"
      className="mx-auto w-full max-w-162"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onBackToAll ?? onCancel}
        disabled={create.isPending}
        className="-ms-2 mb-3.5"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All KPIs
      </Button>
      {/* DS keep: SectionHeader puts the badge on the title row and fixes an mb-6 root, so it cannot draw the badge on the description line with a 16px gap. */}
      <div className="mb-4 space-y-1.5">
        <h1
          id="create-kpi-heading"
          className="text-heading-3 tracking-tight text-pb-text"
        >
          Create KPI
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>Starts as Draft</Badge>
          <p className="text-body text-pb-text-secondary">
            Assign an owner and a target to improve before activating it.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3.5">
        <FormSection title="KPI details">
          <FormField
            label="Name"
            required
            error={nameError ?? validation.errors.name}
            hint="A short, unambiguous measure name."
          >
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => setNameTouched(true)}
              maxLength={255}
              placeholder="e.g. Platform Team Throughput"
            />
          </FormField>
          <FormField label="Description" error={validation.errors.description}>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              maxLength={500}
              showCount
              placeholder="What this KPI measures and why it matters."
            />
          </FormField>
          <FormField
            label="KPI Template"
            error={validation.errors.definitionId}
            hint="Optional. Start with a predefined KPI and customize it — it pre-fills unit, direction and category."
          >
            {/* `clearLabel` names the field it clears. This one is "KPI
                Template" on screen and in both of its validation messages;
                "Clear Type" was the last place the older wording survived, so
                the button read as if it belonged to a control that is not on
                the page. */}
            <ClearableCombobox
              options={typeOptions}
              value={definitionId}
              onChange={handleTypeChange}
              placeholder="Search KPI templates…"
              emptyMessage="No KPI templates found"
              clearLabel="Clear KPI Template"
            />
          </FormField>
          <FormField label="Category" error={validation.errors.categoryCode}>
            <ClearableCombobox
              options={categoryOptions}
              value={categoryCode}
              onChange={setCategoryCode}
              placeholder="Search categories…"
              emptyMessage="No categories found"
              clearLabel="Clear category"
            />
          </FormField>
        </FormSection>

        <FormSection
          title="Measurement"
          description="How this KPI is expressed and read."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Unit"
              required={measurementRequired}
              error={unitError ?? validation.errors.unit}
            >
              <Input
                value={unit}
                onChange={(event) => setUnit(event.target.value)}
                maxLength={64}
                placeholder="e.g. %, hours, units"
              />
            </FormField>
            <FormField
              label="Direction"
              required={measurementRequired}
              error={directionError ?? validation.errors.direction}
            >
              <Select
                options={DIRECTION_OPTIONS}
                value={direction}
                onChange={setDirection}
                placeholder="Select a direction"
              />
            </FormField>
          </div>
          <FormField
            label="Reporting frequency"
            error={validation.errors.reportingFrequency}
          >
            <Select
              options={FREQUENCY_OPTIONS}
              value={reportingFrequency}
              onChange={setReportingFrequency}
              placeholder="Select a frequency"
            />
          </FormField>
        </FormSection>

        <FormSection title="Ownership">
          <FormField
            label="Owner"
            error={validation.errors.ownerUserId}
            hint="Search people by name or email. The owner records values."
          >
            <PeopleField
              value={owner}
              onChange={setOwner}
              source={peopleSource}
              label="Owner"
            />
          </FormField>
        </FormSection>

        <Accordion
          items={[
            {
              id: "tti",
              title: (
                <span className="flex flex-wrap items-baseline gap-2">
                  <span>Target to improve</span>
                  <span className="text-dense-1 font-normal text-pb-text-muted">
                    Optional — set it now or later
                  </span>
                </span>
              ),
              content: targetSection,
            },
          ]}
        />

        {submitted &&
        (ttiIncomplete || baselineNotNumber || targetNotNumber) ? (
          <Alert tone="critical" title="Finish the target to improve">
            {ttiIncomplete
              ? "A target to improve needs a baseline value, a target value and a date. Clear all three to create the KPI without one."
              : "Baseline and target values must be numbers — digits only, up to 4 decimal places."}
          </Alert>
        ) : null}

        {/* Kept hand-built (not SettingsSaveBar): that part is a
            reset/save pair with a persistent saved/unsaved hint for
            editing an EXISTING draft — "Reset to defaults" and a save
            state have no meaning for a one-time Cancel/Create flow. */}
        <div className="mt-5.5 flex flex-wrap items-center gap-3 border-t border-pb-border/60 pt-4.5">
          <span className="me-auto flex items-center gap-1.75 text-body-small text-pb-text-muted">
            <span
              aria-hidden="true"
              className="h-1.75 w-1.75 rounded-full bg-pb-text-muted"
            />
            {definitionId
              ? "Only Name is required — the KPI Template sets Unit and Direction."
              : "Only Name, Unit and Direction are required."}
          </span>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
          >
            Create KPI
          </Button>
        </div>
      </form>
    </section>
  );
}
